<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref } from 'vue';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface ItemProps {
  subtitle?: string;
  titleIcon?: string;
}

export interface Item {
  value: object | string | number;
  title: string;
  props?: ItemProps;
}

export interface Props {
  modelValue?: Item | object | string | number | null;
  size?: FieldSize;
  items?: readonly Item[] | readonly string[] | readonly number[];
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  items: () => [],
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    :items="props.items"
    :class="[
      'g-v-text-field',
      $style.kSingleAutocompleteField,
      fieldStyles[sizeClass],
    ]"
    data-testid="kSingleAutocompleteField"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    auto-select-first
    :filter-keys="['title', 'props.subtitle']"
    transition="slide-y-transition"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        v-bind="itemData.props"
      >
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.kSingleAutocompleteField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
